import { useCallback, useEffect, useLayoutEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Box } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { hapticSelection } from "../../../../lib/haptics";
import { focusRingSx } from "../../overviewPanel";
import { TABULAR, type TonalTokens } from "../../../polish/tokens";
import {
  applyFeedHead,
  FEED_PAGE_SIZE,
  useEarningsFeedHead,
} from "../hooks/useEarningsFeed";
import { countNew, newItemsLabel } from "./feedModel";
import type { EarningsFeedItem, WatchlistEarningsFeedResponse } from "./types";

/** Within this of the top, the reader is "at the top": new items apply at once. */
export const AT_TOP_PX = 8;

const atTop = (): boolean => window.scrollY < AT_TOP_PX;

/** The column's horizontal centre, so the fixed pill sits over the feed and not the viewport. */
const useCentre = (column: HTMLElement | null, measure: boolean) => {
  const [centre, setCentre] = useState<number | null>(null);
  useLayoutEffect(() => {
    if (column === null || !measure) return undefined;
    const update = () => {
      const rect = column.getBoundingClientRect();
      setCentre(rect.left + rect.width / 2);
    };
    update();
    window.addEventListener("resize", update);
    const observer =
      typeof ResizeObserver === "undefined" ? null : new ResizeObserver(update);
    observer?.observe(column);
    return () => {
      window.removeEventListener("resize", update);
      observer?.disconnect();
    };
  }, [column, measure]);
  return centre;
};

interface NewItemsPillProps {
  watchlistId: string | null;
  /** The feed's first page: the head starts from it, and applies only when it differs and is newer than the feed. */
  firstPage: WatchlistEarningsFeedResponse;
  /** When the feed's data last changed. */
  feedUpdatedAt: number;
  /** Every card on screen: new means a report not among them. */
  cards: EarningsFeedItem[];
  /** The hub is the view showing: only then does it poll. */
  active: boolean;
  /** The feed's column, to centre the pill over it. */
  column: HTMLElement | null;
  /** CSS `top` for the pill: clear of a fixed top bar. */
  offsetTop: string;
  tokens: TonalTokens;
  reduced: boolean;
}

/**
 * Polls the feed's first page every minute (the head). When it differs from
 * what's shown (a report that has just appeared, or a card whose counts
 * moved) and the reader is at the top, it applies at once; scrolled, a new
 * report is offered as `n new` over the column until they tap it or scroll
 * back up. Nothing in the rendered list moves until then.
 */
export const NewItemsPill = ({
  watchlistId,
  firstPage,
  feedUpdatedAt,
  cards,
  active,
  column,
  offsetTop,
  tokens,
  reduced,
}: NewItemsPillProps) => {
  const queryClient = useQueryClient();
  const head = useEarningsFeedHead({
    watchlistId,
    enabled: active,
    seed: firstPage,
  });
  const headPage = head.data;
  const usable = headPage !== undefined && !headPage.locked;
  const count = usable ? countNew(headPage.data, cards) : 0;
  // Structural sharing keeps an unchanged head's object, so a new object is
  // a change; the feed's own later refetch is never overwritten by an older head.
  const changed =
    usable && headPage !== firstPage && head.dataUpdatedAt > feedUpdatedAt;
  const [scrolled, setScrolled] = useState(() => !atTop());

  const apply = useCallback(() => {
    if (headPage === undefined) return;
    applyFeedHead(queryClient, watchlistId, headPage);
  }, [queryClient, watchlistId, headPage]);

  // At the top there is nothing to keep still: the head goes in at once, and
  // so it does when the reader scrolls back up. The position is read live.
  useEffect(() => {
    if (!changed) return undefined;
    const onScroll = () => {
      const top = atTop();
      setScrolled(!top);
      if (top) apply();
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [changed, apply]);

  const visible = active && changed && count > 0 && scrolled;
  const centre = useCentre(column, visible);

  return createPortal(
    <Box
      aria-live="polite"
      data-testid="new-items-region"
      sx={{
        position: "fixed",
        top: offsetTop,
        left: centre ?? "50%",
        transform: "translateX(-50%)",
        zIndex: 1050,
        pointerEvents: "none",
      }}
    >
      {visible && (
        <Box
          component="button"
          type="button"
          data-testid="new-items-pill"
          onClick={() => {
            hapticSelection();
            window.scrollTo({ top: 0, behavior: reduced ? "auto" : "smooth" });
            apply();
          }}
          sx={{
            all: "unset",
            ...focusRingSx,
            ...TABULAR,
            pointerEvents: "auto",
            boxSizing: "border-box",
            display: "inline-flex",
            alignItems: "center",
            minHeight: 36,
            px: 2,
            borderRadius: "999px",
            cursor: "pointer",
            fontFamily: "inherit",
            fontSize: "0.8125rem",
            fontWeight: 600,
            color: tokens.gainText,
            backgroundColor: tokens.raised,
            WebkitTapHighlightColor: "transparent",
            "@media (pointer: coarse)": { minHeight: 44 },
          }}
        >
          {newItemsLabel(count, FEED_PAGE_SIZE)}
        </Box>
      )}
    </Box>,
    document.body,
  );
};
